import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Icon, Modal } from "semantic-ui-react";

import { createFormHelper } from "lib/Form";
import { CreateUpdateKioskForm, KioskPrivateKeyModal } from "modules/KioskDashboard/components/CreateUpdateKioskModal/components";
import "modules/KioskDashboard/components/CreateUpdateKioskModal/CreateUpdateKioskModal.scss";
import {
  useCreateKioskMutation,
  useUpdateKioskMutation,
  useUploadConfirmationImageMutation,
  useUploadStandbyImageMutation
} from "modules/KioskDashboard/components/CreateUpdateKioskModal/hooks";

import type { Kiosk } from "models";
import type { CreateUpdateKioskPayload } from "modules/KioskDashboard/components/CreateUpdateKioskModal/hooks";

type KioskCreateUpdateModalProps = {
  isOpen: boolean;
  kioskToUpdate?: Kiosk | null;
  onClose: () => void;
};

const FormHelper = createFormHelper<CreateUpdateKioskPayload>();

export const CreateUpdateKioskModal = ({ isOpen, onClose, kioskToUpdate }: KioskCreateUpdateModalProps) => {
  const { t } = useTranslation();

  const [hasErrors, setHasErrors] = useState(false);
  const [privateKey, setPrivateKey] = useState("");

  const { mutateAsync: createKiosk, data: createdKiosk, isPending: isCreateKioskPending, isSuccess: isCreateKioskSuccess } = useCreateKioskMutation();
  const { mutateAsync: updateKiosk, isPending: isUpdateKioskPending, isSuccess: isUpdateKioskSuccess } = useUpdateKioskMutation();
  const { mutateAsync: uploadStandByImg, data: standByImgUrl, isPending: isUploadingStandByImg, reset: resetStandByImg } = useUploadStandbyImageMutation();
  const {
    mutateAsync: uploadConfirmationImg,
    data: confirmationImgUrl,
    isPending: isUploadingConfirmationImg,
    reset: resetConfirmationImg
  } = useUploadConfirmationImageMutation();

  useEffect(() => {
    if (isUpdateKioskSuccess) onClose();
    if (isCreateKioskSuccess) setPrivateKey(createdKiosk.private_key);
  }, [isUpdateKioskSuccess, isCreateKioskSuccess]);

  const handleClose = () => {
    onClose();
    resetConfirmationImg();
    resetStandByImg();
    setHasErrors(false);
  };

  const isFormInvalid = ({ name, confirmation_title, is_custom_web_page_enabled, custom_web_page_display_text }: CreateUpdateKioskPayload) => {
    if (!name || !confirmation_title || (!confirmationImgUrl && !kioskToUpdate?.confirmation_image_url) || (is_custom_web_page_enabled && !custom_web_page_display_text))
      return true;
    return false;
  };

  const handleSubmit = async (payload: CreateUpdateKioskPayload) => {
    if (isFormInvalid(payload)) {
      setHasErrors(true);
      return;
    }

    kioskToUpdate
      ? await updateKiosk({
          ...kioskToUpdate,
          ...payload,
          stand_by_image_url: standByImgUrl || payload.stand_by_image_url || "",
          confirmation_image_url: confirmationImgUrl || payload.confirmation_image_url || ""
        })
      : await createKiosk({ ...payload, stand_by_image_url: standByImgUrl ?? "", confirmation_image_url: confirmationImgUrl ?? "" });
    handleClose();
  };

  return (
    <>
      <Modal open={isOpen} className="CreateUpdateKioskModal" onClose={handleClose}>
        <Modal.Header>
          <h3>{kioskToUpdate ? t("v8_edit_kiosk", "Edit Kiosk") : t("v8_add_kiosk", "Add Kiosk")}</h3>
        </Modal.Header>
        <FormHelper.Form
          onSubmit={handleSubmit}
          defaultValues={{
            name: kioskToUpdate?.name ?? "",
            confirmation_title: kioskToUpdate?.confirmation_title ?? "",
            is_custom_web_page_enabled: Boolean(kioskToUpdate?.is_custom_web_page_enabled),
            confirmation_image_url: kioskToUpdate?.confirmation_image_url ?? "",
            stand_by_image_url: kioskToUpdate?.stand_by_image_url ?? "",
            custom_web_page_display_text: kioskToUpdate?.custom_web_page_display_text ?? ""
          }}
        >
          <Modal.Content>
            <CreateUpdateKioskForm
              hasErrors={hasErrors}
              standByImageUrl={kioskToUpdate?.stand_by_image_url || standByImgUrl || ""}
              confirmImageUrl={confirmationImgUrl || kioskToUpdate?.confirmation_image_url || ""}
              isUploadingStandByImagePending={isUploadingStandByImg}
              isUploadingConfirmationImagePending={isUploadingConfirmationImg}
              uploadStandByImg={uploadStandByImg}
              uploadConfirmationImg={uploadConfirmationImg}
            />
          </Modal.Content>
          <Modal.Actions>
            <div className="kiosk-create-update-action-buttons-container">
              <Button className="light" onClick={handleClose} disabled={isCreateKioskPending || isUpdateKioskPending}>
                {t("v8_cancel", "Cancel")}
                <Icon className="xmark" />
              </Button>

              <Button type="submit" color="green" disabled={isCreateKioskPending || isUpdateKioskPending} loading={isCreateKioskPending || isUpdateKioskPending}>
                {t("v8_save", "Save")}
                <Icon className="check" />
              </Button>
            </div>
          </Modal.Actions>
        </FormHelper.Form>
      </Modal>
      <KioskPrivateKeyModal privateKey={privateKey} onClose={() => setPrivateKey("")} />
    </>
  );
};
